import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Check, ChevronsUpDown } from "lucide-react";

import FormModal from "@/components/v2/molecules/admin/shared/FormModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import CustomDatePicker from "@/components/CustomDatePicker";
import { cn } from "@/lib/utils";

import { PropertyContract, useCreateContract, useUpdateContract } from "@/hooks/useContracts";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";

const contractSchema = z.object({
  organization_id: z.string().optional(),
  property_id: z.string().min(1, "La propiedad es obligatoria"),
  operation_start_date: z.string().min(1, "La fecha de inicio es obligatoria"),
  cleaning_rate: z.coerce.number().min(0, "Debe ser mayor o igual a 0"),
  administration_rate: z.coerce.number().min(0, "Debe ser mayor o igual a 0"),
  administration_commission: z.coerce.number().min(0, "Debe ser mayor o igual a 0").max(100, "Máximo 100%"),
  promotional_commission: z.coerce.number().optional(),
  promotion_end_date: z.string().optional(),
  status: z.enum(["active", "cancelled"]),
  cancelled_at: z.string().optional(),
  admin_type: z.enum(["Propio", "Renta", "Administración"]),
  notes: z.string().optional(),
  rental_payment_day: z.coerce.number().optional(),
  rental_monthly_amount: z.coerce.number().optional(),
  rental_iva_percentage: z.coerce.number().optional(),
});

type ContractFormData = z.infer<typeof contractSchema>;

interface ContractFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract?: PropertyContract | null;
}

const ContractFormModal = ({ isOpen, onClose, contract }: ContractFormModalProps) => {
  const { data: properties } = useProperties();
  const { data: organizations } = useOrganizations();
  const createMutation = useCreateContract();
  const updateMutation = useUpdateContract();

  const [isPropertyOpen, setIsPropertyOpen] = useState(false);
  const [isOrganizationOpen, setIsOrganizationOpen] = useState(false);

  const form = useForm<ContractFormData>({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      organization_id: "",
      property_id: "",
      operation_start_date: "",
      cleaning_rate: 0,
      administration_rate: 0,
      administration_commission: 0,
      promotional_commission: undefined,
      promotion_end_date: "",
      status: "active",
      cancelled_at: "",
      admin_type: "Propio",
      notes: "",
      rental_payment_day: undefined,
      rental_monthly_amount: undefined,
      rental_iva_percentage: 16,
    },
  });

  const watchedAdminType = form.watch("admin_type");
  const watchedPromotionalCommission = form.watch("promotional_commission");
  const watchedStatus = form.watch("status");

  // Auto-fill cancelled_at with today when status changes to cancelled
  useEffect(() => {
    if (watchedStatus === 'cancelled' && !form.getValues('cancelled_at')) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      form.setValue('cancelled_at', `${year}-${month}-${day}`);
    }
    if (watchedStatus === 'active') {
      form.setValue('cancelled_at', '');
    }
  }, [watchedStatus, form]);

  useEffect(() => {
    if (contract && isOpen) {
      form.reset({
        organization_id: contract.organization_id || "",
        property_id: contract.property_id,
        operation_start_date: contract.operation_start_date,
        cleaning_rate: contract.cleaning_rate,
        administration_rate: contract.administration_rate,
        administration_commission: contract.administration_commission,
        promotional_commission: contract.promotional_commission,
        promotion_end_date: contract.promotion_end_date || "",
        status: contract.status,
        cancelled_at: contract.cancelled_at || "",
        admin_type: contract.admin_type,
        notes: contract.notes || "",
        rental_payment_day: contract.rental_payment_day,
        rental_monthly_amount: contract.rental_monthly_amount,
        rental_iva_percentage: contract.rental_iva_percentage || 16,
      });
    } else if (!isOpen) {
      form.reset({
        organization_id: "",
        property_id: "",
        operation_start_date: "",
        cleaning_rate: 0,
        administration_rate: 0,
        administration_commission: 0,
        promotional_commission: undefined,
        promotion_end_date: "",
        status: "active",
        cancelled_at: "",
        admin_type: "Propio",
        notes: "",
        rental_payment_day: undefined,
        rental_monthly_amount: undefined,
        rental_iva_percentage: 16,
      });
    }
  }, [contract, isOpen, form]);

  const onSubmit = async (data: ContractFormData) => {
    const payload = {
      organization_id: data.organization_id || undefined,
      property_id: data.property_id,
      operation_start_date: data.operation_start_date,
      cleaning_rate: data.cleaning_rate,
      administration_rate: data.administration_rate,
      administration_commission: data.administration_commission,
      promotional_commission: data.promotional_commission || undefined,
      promotion_end_date: data.promotion_end_date || undefined,
      status: data.status,
      cancelled_at: data.status === 'cancelled' ? (data.cancelled_at || undefined) : undefined,
      admin_type: data.admin_type,
      notes: data.notes || undefined,
      rental_payment_day: data.admin_type === 'Renta' ? data.rental_payment_day : undefined,
      rental_monthly_amount: data.admin_type === 'Renta' ? data.rental_monthly_amount : undefined,
      rental_iva_percentage: data.admin_type === 'Renta' ? data.rental_iva_percentage : undefined,
    };

    try {
      if (contract) {
        await updateMutation.mutateAsync({ id: contract.id, ...payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch (error) {
      console.error("Error al guardar contrato:", error);
    }
  };

  const getPropertyName = (propertyId: string) => {
    const property = properties?.find(p => p.id === propertyId);
    return property?.shortName || property?.title || propertyId;
  };

  const getOrganizationName = (organizationId: string) => {
    const organization = organizations?.find(o => o.id === organizationId);
    return organization?.name || organizationId;
  };

  const availableProperties = useMemo(() => {
    return properties?.filter(property => {
      if (contract && property.id === contract.property_id) {
        return true;
      }
      // Solo mostrar propiedades que no tienen un contrato activo
      // For now we allow all properties as per original logic comment
      return true;
    }) || [];
  }, [properties, contract]);

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={contract ? "Editar Contrato" : "Nuevo Contrato"}
      form={form}
      onSubmit={onSubmit}
      isLoading={isLoading}
      submitLabel={contract ? "Actualizar" : "Crear"}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Organización</FormLabel>
              <Popover open={isOrganizationOpen} onOpenChange={setIsOrganizationOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isOrganizationOpen}
                      className="w-full justify-between"
                    >
                      {field.value
                        ? getOrganizationName(field.value)
                        : "Seleccionar organización..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Buscar organización..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron organizaciones.</CommandEmpty>
                      <CommandGroup>
                        {organizations?.map((organization) => (
                          <CommandItem
                            key={organization.id}
                            value={organization.name}
                            onSelect={() => {
                              form.setValue("organization_id", organization.id);
                              setIsOrganizationOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                field.value === organization.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {organization.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="property_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Propiedad *</FormLabel>
              <Popover open={isPropertyOpen} onOpenChange={setIsPropertyOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isPropertyOpen}
                      className="w-full justify-between"
                    >
                      {field.value
                        ? getPropertyName(field.value)
                        : "Seleccionar propiedad..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Buscar propiedad..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron propiedades.</CommandEmpty>
                      <CommandGroup>
                        {availableProperties.map((property) => (
                          <CommandItem
                            key={property.id}
                            value={property.shortName || property.title}
                            onSelect={() => {
                              form.setValue("property_id", property.id);
                              setIsPropertyOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                field.value === property.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {property.shortName || property.title}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="operation_start_date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Inicio de Operación *</FormLabel>
              <CustomDatePicker
                selected={field.value ? new Date(field.value + 'T12:00:00') : undefined}
                onSelect={(date) => {
                  if (date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    field.onChange(`${year}-${month}-${day}`);
                  }
                }}
                placeholder="Seleccionar fecha"
                className="w-full h-fit"
                isAdminContext
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="cleaning_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tarifa de Limpieza (MXN) *</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="administration_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tarifa de Administración (MXN) *</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="administration_commission"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comisión de Administración (%) *</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" max="100" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="promotional_commission"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comisión Promocional (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {(watchedPromotionalCommission !== undefined && watchedPromotionalCommission > 0) && (
          <FormField
            control={form.control}
            name="promotion_end_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Vigencia de Promoción</FormLabel>
                <CustomDatePicker
                  selected={field.value ? new Date(field.value + 'T12:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      field.onChange(`${year}-${month}-${day}`);
                    } else {
                      field.onChange("");
                    }
                  }}
                  placeholder="Seleccionar fecha de fin"
                  className="w-full h-fit"
                  isAdminContext
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Estado *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchedStatus === 'cancelled' && (
          <FormField
            control={form.control}
            name="cancelled_at"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Fecha de Cancelación</FormLabel>
                <CustomDatePicker
                  selected={field.value ? new Date(field.value + 'T12:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      field.onChange(`${year}-${month}-${day}`);
                    } else {
                      field.onChange("");
                    }
                  }}
                  placeholder="Seleccionar fecha de cancelación"
                  className="w-full h-fit"
                  isAdminContext
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="admin_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Administración *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Propio">Propio</SelectItem>
                  <SelectItem value="Renta">Renta</SelectItem>
                  <SelectItem value="Administración">Administración</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchedAdminType === 'Renta' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="rental_payment_day"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Día de Pago *</FormLabel>
                  <Select
                    onValueChange={(val) => field.onChange(parseInt(val))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Día del mes" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="max-h-[200px]">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          Día {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rental_monthly_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Monto Mensual (MXN) *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rental_iva_percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IVA (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      {...field}
                      value={field.value ?? 16}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notas</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Notas adicionales sobre el contrato..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default ContractFormModal;